/**
 * OneSignal Web Push Notification Service
 *
 * This service handles the initialization and management of OneSignal web push notifications.
 * It automatically uses the correct app ID based on the environment (local, staging, production).
 */

import { captureException, captureMessage } from "@/config/sentry";

declare global {
  interface Window {
    OneSignalDeferred?: any;
    OneSignal?: any;
  }
}

export interface OneSignalConfig {
  appId: string;
  allowLocalhostAsSecureOrigin?: boolean;
  serviceWorkerParam?: {
    scope: string;
  };
  notifyButton?: {
    enable: boolean;
  };
  promptOptions?: {
    slidedown?: {
      enabled: boolean;
      autoPrompt?: boolean;
    };
  };
}

class OneSignalService {
  private isInitialized = false;
  private appId: string | null = null;
  private initializationPromise: Promise<void> | null = null;
  private unavailableReported = false;

  constructor() {
    // Get OneSignal App ID from environment variables
    this.appId = import.meta.env.VITE_ONESIGNAL_APP_ID || null;
  }

  /**
   * Initialize OneSignal with environment-specific configuration
   */
  async initialize(): Promise<void> {
    // Return existing initialization promise if already initializing
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Return immediately if already initialized
    if (this.isInitialized) {
      return Promise.resolve();
    }

    // No App ID means push cannot work at all in this build. Reject rather than
    // resolve: resolving left every caller pushing a callback into
    // window.OneSignalDeferred, which is only drained once the SDK script loads.
    // With no App ID the script is never loaded, so those promises never settled
    // and the UI reported push as ready. Callers each catch this and degrade to
    // their own safe default.
    if (!this.appId) {
      this._reportUnavailable();
      throw new Error(
        "OneSignal is not configured (VITE_ONESIGNAL_APP_ID is unset)"
      );
    }

    // Create and store initialization promise
    this.initializationPromise = this._initializeOneSignal();
    return this.initializationPromise;
  }

  /**
   * Report a missing App ID once per session. Every failure path in this service
   * degrades silently by design, so without this a misconfigured deploy is
   * invisible in Sentry and looks identical to a working one.
   */
  private _reportUnavailable(): void {
    if (this.unavailableReported) return;
    this.unavailableReported = true;

    console.warn(
      "[OneSignal] VITE_ONESIGNAL_APP_ID is not set — web push is disabled for this session."
    );
    captureMessage(
      "OneSignal disabled: VITE_ONESIGNAL_APP_ID is not configured",
      "warning"
    );
  }

  /** True when an App ID is present, i.e. push can work at all in this build. */
  isConfigured(): boolean {
    return Boolean(this.appId);
  }

  /** True once the SDK has actually loaded and initialised. */
  isReady(): boolean {
    return this.isInitialized;
  }

  private async _initializeOneSignal(): Promise<void> {
    try {
      // Wait for OneSignal SDK to load
      await this._loadOneSignalSDK();

      const isDevelopment = import.meta.env.DEV;

      // Initialize OneSignal with configuration
      window.OneSignalDeferred = window.OneSignalDeferred || [];

      // Return a promise that resolves when init completes
      return new Promise((resolve, reject) => {
        window.OneSignalDeferred.push(async (OneSignal: any) => {
          try {
            await OneSignal.init({
              appId: this.appId!,
              allowLocalhostAsSecureOrigin: isDevelopment,
              // Disable the notify button - using custom UI instead
              notifyButton: {
                enable: false,
              },
              // Disable OneSignal's default slidedown - using custom toast UI
              promptOptions: {
                slidedown: {
                  enabled: false,
                  autoPrompt: false,
                },
              },
            });

            this.isInitialized = true;

            // Set up event listeners
            this._setupEventListeners(OneSignal);

            resolve();
          } catch (error) {
            reject(error);
          }
        });
      });
    } catch (error) {
      this.isInitialized = false;
      this.initializationPromise = null;
      throw error;
    }
  }

  /**
   * Load OneSignal SDK dynamically
   */
  private _loadOneSignalSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.OneSignalDeferred || window.OneSignal) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src =
        "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
      script.defer = true;

      script.onload = () => resolve();
      script.onerror = () => {
        reject(new Error("Failed to load OneSignal SDK"));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Set up OneSignal event listeners
   */
  private _setupEventListeners(OneSignal: any): void {
    try {
      // Listen for notification clicks (when user clicks on a notification)
      OneSignal.Notifications.addEventListener("click", (event: any) => {
        // Navigate to URL if provided
        const url = event.notification?.launchURL || event.notification?.url;
        if (url) {
          window.location.href = url;
        }
      });

      // CRITICAL: Listen for foreground notifications (notifications received while app is open)
      // This event allows notifications to be displayed even when the app is in focus
      OneSignal.Notifications.addEventListener(
        "foregroundWillDisplay",
        (event: any) => {
          // The notification will be displayed automatically by the browser
          // Customize the notification here if needed:
          // event.notification.heading = "Custom Title";
          // event.notification.content = "Custom Content";
        }
      );
    } catch (error) {
      captureException(error, { scope: "onesignal.setupEventListeners" });
    }
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      return new Promise((resolve) => {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (OneSignal: any) => {
          try {
            // Check if already granted
            const currentPermission = await OneSignal.Notifications.permission;

            if (currentPermission === true) {
              resolve(true);
              return;
            }

            // Request permission
            const permission =
              await OneSignal.Notifications.requestPermission();
            resolve(permission === true);
          } catch (error) {
            captureException(error, { scope: "onesignal.requestPermission" });
            resolve(false);
          }
        });
      });
    } catch (error) {
      return false;
    }
  }

  /**
   * Set the external user ID (your backend user ID)
   */
  async setExternalUserId(userId: string): Promise<void> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      return new Promise((resolve) => {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (OneSignal: any) => {
          try {
            await OneSignal.login(userId);
            resolve();
          } catch (error) {
            resolve();
          }
        });
      });
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Remove the external user ID (on logout)
   */
  async removeExternalUserId(): Promise<void> {
    try {
      if (!this.isInitialized) {
        return;
      }

      return new Promise((resolve) => {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (OneSignal: any) => {
          try {
            await OneSignal.logout();
            resolve();
          } catch (error) {
            resolve();
          }
        });
      });
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Check if the user has granted notification permission
   */
  async hasPermission(): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      return new Promise((resolve) => {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (OneSignal: any) => {
          try {
            const permission = await OneSignal.Notifications.permission;
            resolve(permission === true);
          } catch (error) {
            resolve(false);
          }
        });
      });
    } catch (error) {
      return false;
    }
  }

  /**
   * Get the current browser notification permission status synchronously
   * This doesn't require OneSignal to be initialized
   */
  getBrowserPermissionStatus(): NotificationPermission {
    if (!this.isSupported() || !("Notification" in window)) {
      return "denied";
    }
    return Notification.permission;
  }

  /**
   * Check if browser permission is granted (synchronous check)
   */
  hasBrowserPermission(): boolean {
    return this.getBrowserPermissionStatus() === "granted";
  }

  /**
   * Get the OneSignal player ID (device-specific ID)
   */
  async getPlayerId(): Promise<string | null> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      return new Promise((resolve) => {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (OneSignal: any) => {
          try {
            const playerId = await OneSignal.User.PushSubscription.id;
            resolve(playerId || null);
          } catch (error) {
            resolve(null);
          }
        });
      });
    } catch (error) {
      return null;
    }
  }

  /**
   * Show a slidedown prompt for notifications
   */
  async showNativePrompt(): Promise<void> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      return new Promise((resolve) => {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (OneSignal: any) => {
          try {
            const permission = await OneSignal.Notifications.permission;

            // Only show if permission hasn't been decided yet
            if (
              permission === null ||
              permission === undefined ||
              permission === false
            ) {
              await OneSignal.Slidedown.promptPush();
            }
            resolve();
          } catch (error) {
            resolve();
          }
        });
      });
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Check if OneSignal is supported in the current browser
   */
  isSupported(): boolean {
    return (
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window
    );
  }
}

// Export singleton instance
export const oneSignalService = new OneSignalService();
